/**
 * Imperative arrange stereo RMS overlays — rAF only, no React per frame.
 * Levels are drawn only on the pattern currently under the playhead.
 */
import { meterHeightFromRms, type EngineTelemetry } from "../../../../src/lib/audio";
import { getArrangePlayingPatternId } from "./arrangePlayhead";
import { createMeterClock, smoothMeterLevel } from "./meterSmooth";

type MeterPair = {
  patternId: string;
  track: number;
  left: HTMLElement;
  right: HTMLElement;
  shownL: number;
  shownR: number;
};

/** All registered cell overlays (pattern × track). */
const allBars: MeterPair[] = [];
const clock = createMeterClock();

let rafId = 0;
let getTelemetry: (() => EngineTelemetry) | null = null;
let isPlaying: (() => boolean) | null = null;

export function registerArrangeMeterBars(
  patternId: string,
  track: number,
  left: HTMLElement,
  right: HTMLElement,
): () => void {
  const pair: MeterPair = { patternId, track, left, right, shownL: 0, shownR: 0 };
  allBars.push(pair);
  return () => {
    const index = allBars.indexOf(pair);
    if (index >= 0) {
      allBars.splice(index, 1);
    }
  };
}

export function setArrangeMeterPump(options: {
  getTelemetry: () => EngineTelemetry;
  isPlaying: () => boolean;
} | null): void {
  getTelemetry = options?.getTelemetry ?? null;
  isPlaying = options?.isPlaying ?? null;
  if (!options) {
    stopPump();
    snapClear();
    return;
  }
  clock.reset();
  startPump();
}

function startPump(): void {
  if (rafId !== 0) {
    return;
  }
  const tick = () => {
    rafId = requestAnimationFrame(tick);
    paint();
  };
  rafId = requestAnimationFrame(tick);
}

function stopPump(): void {
  if (rafId === 0) {
    return;
  }
  cancelAnimationFrame(rafId);
  rafId = 0;
}

function snapClear(): void {
  for (const pair of allBars) {
    pair.shownL = 0;
    pair.shownR = 0;
    pair.left.style.transform = "scaleY(0)";
    pair.right.style.transform = "scaleY(0)";
  }
}

function paint(): void {
  if (!getTelemetry || !isPlaying) {
    return;
  }
  const dt = clock.dt();
  const playing = isPlaying();
  const playingPatternId = playing ? getArrangePlayingPatternId() : null;
  const telemetry = playing && playingPatternId ? getTelemetry() : null;

  for (const pair of allBars) {
    const active = telemetry != null && pair.patternId === playingPatternId;
    const levels = active ? telemetry.tracks[pair.track] : undefined;
    const targetL = active ? meterHeightFromRms(levels?.rmsL ?? 0) : 0;
    const targetR = active ? meterHeightFromRms(levels?.rmsR ?? 0) : 0;
    pair.shownL = smoothMeterLevel(pair.shownL, targetL, dt);
    pair.shownR = smoothMeterLevel(pair.shownR, targetR, dt);
    pair.left.style.transform = `scaleY(${pair.shownL})`;
    pair.right.style.transform = `scaleY(${pair.shownR})`;
  }
}
